import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord,
  OperationMetadata,
  ProjectSpecificOperationSummary,
  ResponsibilityType,
  Step,
  StepCollection,
  StepCollectionInstance,
  StepInstance,
  TypeSwitch,
} from '../../../models/projects/operation.models';
import { DbContext, DbTable } from '../../data/dbcontext.service';
import { VesselService } from '../../vessel/vessel.service';
import { SearchService } from '../../search/search.service';
import { DateService } from '../../date/date.service';
import { DateParserService } from '../../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { cloneDeep } from 'lodash';
import { StartAndEnd } from '../../../models/projects/validation.models';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { OperationMetadataRequest } from '../../../models/metadata/metadata.models';
import { SetupChangeLogRecord } from '../../../models/setup/setup.models';
import { VoyageLogService } from '../../voyage-log/voyage-log.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { SetupDataService } from '../../setup/setup-data.service';

export interface OperationSearchResult {
  rank: number;
  explanation?: {
    label: string;
    text: string;
    additionalCount: number;
    title: string;
  };
}

@Injectable()
export class OperationService {
  config = AppConfig.settings;
  private readonly operationsDataTable: DbTable;
  private readonly stepCollectionsDataTable: DbTable;
  private readonly dailyLogTable: DbTable;
  private readonly psoTable: DbTable;
  private readonly setupChangeLogTable: DbTable;
  private readonly validationSettingsByDay: { [key: string]: Promise<OperationMetadata> } = {};

  constructor(
    private http: HttpClient,
    private vesselService: VesselService,
    private searchService: SearchService,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private dbContext: DbContext,
    private timeZoneService: TimeZoneService,
    private voyageLogService: VoyageLogService,
    private setupDataService: SetupDataService
  ) {
    this.operationsDataTable = this.dbContext.tables.find(t => t.name === 'OperationsData');
    this.stepCollectionsDataTable = this.dbContext.tables.find(t => t.name === 'StepCollectionsData');
    this.dailyLogTable = this.dbContext.tables.find(t => t.name === 'DailyLogData');
    this.psoTable = this.dbContext.tables.find(t => t.name === 'ProjectSpecificOperationInstance');
    this.setupChangeLogTable = this.dbContext.tables.find(t => t.name === 'SetupChangeLogData');
  }

  getOperationsMetadata(date: PtLuxonDate, setupData?: SetupChangeLogRecord[]): Promise<OperationMetadata> {
    return this.vesselService
      .getEditor()
      .then(vessel => {
        return this.getStageIdsForDay(date, setupData)
          .then(async ids => {
            if (ids == null) {
              return null;
            }

            const request = new OperationMetadataRequest();
            request.stageIds = ids;
            request.vesselId = vessel.id;

            request.dailyLogDate = this.dateParserService.removeOffsetFromLuxonDate(date.dateTime);

            const url = `${this.config.apiUrl}api/operations/metadata`;
            const result = await this.http
              .post<OperationMetadata>(url, request)
              .toPromise();

            return await this.deserializeOperationMetaData(result);
          });
      });
  }

  async getStageIdsForDay(date: PtLuxonDate, setupData?: SetupChangeLogRecord[]): Promise<number[]> {
    const data = setupData ? setupData : await this.setupDataService.getSetupChangeLogData();
    const stageChanges = [] as SetupChangeLogRecord[];
    const stageChangesForDay = [] as SetupChangeLogRecord[];

    // loop through data to fix dates separately to the below loop
    for (const row of data) {
      if (row.changeTypeId !== 3) {
        continue;
      }

      stageChanges.push(row);
    }

    const sortedStageChangesBeforeCurrentDate = stageChanges
      .filter(e => e.timeOfChange.dateTime < date.dateTime)
      // sort descending
      .sort((function (a, b) {
        return b.timeOfChange.dateTime.toMillis() - a.timeOfChange.dateTime.toMillis();
      }));
    const latestStageChangeBeforeCurrentDate = sortedStageChangesBeforeCurrentDate[0];
    for (const stageChange of stageChanges) {

      // if the day of the stage change is not equal to the current day, we don't want to include the stage,
      // unless it is the last change from a previous day (as it means it's relevant to the start of the current day)
      const daysEqual = this.dateService.compareDayOffsetsPreApplied(stageChange.timeOfChange.dateTime, date.dateTime);

      if (!daysEqual) {
        // Dates before initial setup have no preceding stage change.
        if (!latestStageChangeBeforeCurrentDate) { continue; }
        // if it's not the latest change from the previous day, then ignore it
        if (stageChange.id != null
          ? stageChange.id !== latestStageChangeBeforeCurrentDate.id
          : stageChange.localId !== latestStageChangeBeforeCurrentDate.localId) {
          continue;
        }
      }

      stageChangesForDay.push(stageChange);
    }

    // No applicable stage means an inactive date; never borrow a future stage.
    const ids = stageChangesForDay
      .map(p => p.newValueId)
      .filter((id, index, array) => array.indexOf(id) === index);

    return ids;
  }

  async saveOperationsData(operationMetadata: OperationMetadata) {
    await this.clearOperationsData();

    for (let i = 0; i < operationMetadata.projectSpecificOperations.length; i++) {
      await this.dbContext.addOrUpdate(this.operationsDataTable,
        operationMetadata.projectSpecificOperations[i].id,
        operationMetadata.projectSpecificOperations[i]);
    }

    for (let i = 0; i < operationMetadata.stepCollections.length; i++) {
      await this.dbContext
        .addOrUpdate(this.stepCollectionsDataTable, operationMetadata.stepCollections[i].id, operationMetadata.stepCollections[i]);
    }
  }

  async clearOperationsData() {
    await this.dbContext.removeAll(this.operationsDataTable);
    await this.dbContext.removeAll(this.stepCollectionsDataTable);
  }

  saveOperationData(operation: ProjectSpecificOperationSummary) {
    this.dbContext.addOrUpdate(this.operationsDataTable, operation.id, operation);
  }

  getOperation(id: number): Promise<ProjectSpecificOperationSummary> {
    return this.dbContext.get(this.operationsDataTable, id);
  }

  private async operationValidationSettingsKey(date: PtLuxonDate): Promise<string> {
    const vessel = await this.vesselService.getEditor();
    return `${vessel.id}:${date.dateTime.toISODate()}`;
  }

  async refreshOperationValidationSettings(date: PtLuxonDate): Promise<OperationMetadata> {
    const key = await this.operationValidationSettingsKey(date);
    return this.loadOperationValidationSettings(key, date);
  }

  private async loadOperationValidationSettings(key: string, date: PtLuxonDate): Promise<OperationMetadata> {
    const request = this.getOperationsMetadata(date);
    this.validationSettingsByDay[key] = request;
    try {
      return await request;
    } catch (error) {
      if (this.validationSettingsByDay[key] === request) {
        delete this.validationSettingsByDay[key];
      }
      throw error;
    }
  }

  async getOperationValidationSettings(instance: DailyLogOperationInstance,
                                       dailyLogDate: PtLuxonDate): Promise<ProjectSpecificOperationSummary> {
    const key = await this.operationValidationSettingsKey(dailyLogDate);
    const metadata = await (this.validationSettingsByDay[key] || this.loadOperationValidationSettings(key, dailyLogDate));
    const settings = metadata?.projectSpecificOperations?.find(operation => operation.id === instance.projectSpecificOperation.id);
    if (!settings) {
      delete this.validationSettingsByDay[key];
      throw new Error('Operation settings are not available for the selected reporting day.');
    }
    return settings;
  }

  async isResponsibilityRequired(instance: DailyLogOperationInstance, dailyLogDate: PtLuxonDate,
                                  metadata: ProjectSpecificOperationSummary): Promise<boolean> {
    // Preserve compatibility with metadata downloaded before stage-specific settings were available.
    if (!metadata?.responsibilityOptionalStageIds) {
      return metadata?.responsibilityRequired !== false;
    }

    const relevantDate = instance.startTime?.dateTime || dailyLogDate?.dateTime;
    if (!relevantDate) {
      return true;
    }

    const setupData = await this.setupDataService.getSetupChangeLogData();
    const activeStage = setupData
      .filter(row => row.changeTypeId === 3 && row.timeOfChange.dateTime <= relevantDate)
      .sort((a, b) => b.timeOfChange.dateTime.toMillis() - a.timeOfChange.dateTime.toMillis())[0];

    return !activeStage || metadata.responsibilityOptionalStageIds.indexOf(activeStage.newValueId) < 0;
  }

  getOperations(): Promise<ProjectSpecificOperationSummary[]> {
    return this.dbContext.getAll(this.operationsDataTable);
  }

  deleteAllProjectSpecificOperationData() {
    return this.dbContext.removeAll(this.psoTable);
  }

  async saveAllProjectSpecificOperationData(operationInstances: DailyLogOperationInstance[]) {
    for (const instance of operationInstances) {
      this.normalizeResponsibilityTypeId(instance);
      await this.localSaveProjectSpecificOperationInstance(instance.id, instance);
    }
  }

  deleteProjectSpecificOperation(id: number) {
    if (!id) {
      return null;
    }

    this.dbContext.remove(this.psoTable, id);

    const url = `${this.config.apiUrl}api/appdata/${id}/deletedailylogoperation`;

    return this.http.delete(url);
  }

  async deleteLocalProjectSpecificOperation(id: number) {
    if (!id) {
      return null;
    }

    await this.dbContext.remove(this.psoTable, id);
  }

  async getOperationInstance(id: number): Promise<DailyLogOperationInstance> {
    let record = await this.dbContext.get(this.psoTable, id);

    if (record) {
      record = await this.deserializeInstanceTimes(record);
    }

    return record;
  }

  getDailyLogRecordTempId(): Promise<number> {
    return this.dbContext.getTempId(this.dailyLogTable).then(id => {
      return <number>id;
    });
  }

  getDailyLogRecordUniqueOrdinal(recordCount: number): Promise<number> {
    const url = `${this.config.apiUrl}api/appdata/getuniqueordinal/${recordCount}`;
    return this.http
      .get<number>(url)
      .toPromise();
  }

  getProjectSpecificOperationTempId(): Promise<number> {
    return this.dbContext.getTempId(this.psoTable).then(id => {
      return <number>id;
    });
  }

  searchOperation(operation: ProjectSpecificOperationSummary, searchString: string): boolean {
    return this.getOperationSearchRank(operation, searchString) > Number.NEGATIVE_INFINITY;
  }

  getOperationSearchRank(operation: ProjectSpecificOperationSummary, searchString: string): number {
    return this.getOperationSearchResult(operation, searchString).rank;
  }

  getOperationSearchResult(operation: ProjectSpecificOperationSummary, searchString: string): OperationSearchResult {
    if (!searchString || !searchString.trim()) {
      return { rank: 0 };
    }

    const coreOperation = operation.operationSummary;
    const normalizedSearchString = searchString.trim().toLowerCase();
    const shortSearch = normalizedSearchString.length <= 2;
    // Weight operation names above supporting metadata so the visible sort favors what users expect to type.
    const fields = [
      { label: '', text: operation.name, weight: 600 },
      { label: 'Activity', text: coreOperation?.name, weight: 500 },
      { label: 'Start', text: coreOperation?.startDefinition, weight: shortSearch ? Number.NEGATIVE_INFINITY : 100 },
      { label: 'End', text: coreOperation?.endDefinition, weight: shortSearch ? Number.NEGATIVE_INFINITY : 0 }
    ].map(field => ({ ...field, rank: this.getWeightedSearchScore(field.text, searchString, field.weight) }))
      .sort((first, second) => second.rank - first.rank);

    // Search each step separately so words from unrelated steps cannot form a match.
    const steps = coreOperation?.stepCollection?.steps || [];
    const matchingSteps = steps
      .map(step => ({ name: step.name, rank: this.getWeightedSearchScore(step.name, searchString, 200) }))
      .filter(step => step.rank > Number.NEGATIVE_INFINITY)
      .sort((first, second) => second.rank - first.rank);
    const bestField = fields[0];
    const operationRank = bestField.rank;
    const bestStepRank = matchingSteps.length ? matchingSteps[0].rank : Number.NEGATIVE_INFINITY;

    if (bestStepRank > operationRank) {
      return {
        rank: bestStepRank,
        explanation: {
          label: 'Step', text: matchingSteps[0].name, additionalCount: matchingSteps.length - 1,
          title: 'Matching steps: ' + matchingSteps.map(step => step.name).join(', ')
        }
      };
    }

    // Visible operation-name matches explain themselves; supporting fields need a label.
    const displayedName = operation.name || coreOperation?.name;
    return {
      rank: operationRank,
      explanation: operationRank > Number.NEGATIVE_INFINITY && bestField.label && bestField.text !== displayedName
        ? { label: bestField.label, text: bestField.text, additionalCount: 0, title: bestField.label + ': ' + bestField.text }
        : undefined
    };
  }

  private getWeightedSearchScore(toSearch: string, searchString: string, weight: number): number {
    // Skip fields entirely when a search mode should not consider them.
    if (weight === Number.NEGATIVE_INFINITY) {
      return Number.NEGATIVE_INFINITY;
    }

    const score = this.searchService.getSearchScore(toSearch, searchString);

    return score >= 0 ? score + weight : Number.NEGATIVE_INFINITY;
  }

  async localSaveProjectSpecificOperationInstance(id: number, dailyLogOperationInstance: DailyLogOperationInstance) {
    dailyLogOperationInstance = this.dateParserService.removeOffsetFromLuxonDates(dailyLogOperationInstance);
    await this.dbContext.addOrUpdate(this.psoTable, <number>id, dailyLogOperationInstance);
  }

  async getProjectSpecificOperationInstance(id: number): Promise<DailyLogOperationInstance> {
    if (!id) {
      return null;
    }

    let operationInstance = await this.dbContext.get(this.psoTable, <number>id) as DailyLogOperationInstance;

    if (operationInstance === null) {
      operationInstance = await this.getProjectSpecificOperationInstanceFromApi(id);
      return operationInstance;
    }

    operationInstance = await this.deserializeInstanceTimes(cloneDeep(operationInstance));

    return cloneDeep(operationInstance);
  }

  async getProjectSpecificOperationInstanceFromApi(id: number): Promise<DailyLogOperationInstance> {
    const url = `${this.config.apiUrl}api/appdata/operation/${id}`;
    const instance = await this.http.get<DailyLogOperationInstance>(url).toPromise();
    return await this.deserializeInstanceTimes(instance);
  }

  async getProjectSpecificOperationInstances(ids: number[]): Promise<DailyLogOperationInstance[]> {
    const operations = await this.dbContext.getManyIn(this.psoTable, ids);

    for (let i = 0; i < operations.length; i++) {
      operations[i] = await this.deserializeInstanceTimes(operations[i]);
    }

    return operations;
  }

  async getDelays(): Promise<DailyLogOperationInstance[]> {
    const operations = await this.getAllProjectSpecificOperationInstances(null);

    const delays = operations.filter(p => p.projectSpecificOperation.operationSummary.classificationName === 'Delay');

    return delays;
  }

  async getDelaysWithinRange(startTime: DateTime, endTime: DateTime): Promise<DailyLogOperationInstance[]> {
    let delays = await this.getDelays();

    delays = delays.filter(p => this.operationInTime(
      startTime,
      endTime,
      p.startTime.dateTime,
      p.endTime.dateTime));

    return delays;
  }

  // preventOffsets used for the daily log summary, where we don't care about getting correct local times, only care about durations
  async getAllProjectSpecificOperationInstancesForDay(
    ptLuxonDate: PtLuxonDate,
    ignoreId?: number,
    contractualOnly?: boolean,
  ): Promise<DailyLogOperationInstance[]> {
    let instances = await this.getAllProjectSpecificOperationInstances(ignoreId) as DailyLogOperationInstance[];

    if (!ptLuxonDate) {
      return [];
    }

    instances = instances
      .filter(p => (p.cancelledTime === null || p.cancelledTime > ptLuxonDate.dateTime) && // make sure any ops cancelled on a previous day are not included
        (((!p.endTime || !p.endTime.dateTime) && p.startTime.dateTime < ptLuxonDate.dateTime)
          || (p.startTime.dateTime < ptLuxonDate.dateTime && p.endTime && p.endTime.dateTime > ptLuxonDate.dateTime)
          || this.dateService.compareDayOffsetsPreApplied(
            p.startTime.dateTime,
            ptLuxonDate.dateTime)
          || this.dateService.compareDayOffsetsPreApplied(
            p.endTime.dateTime,
            ptLuxonDate.dateTime)));

    if (contractualOnly) {
      instances = instances.filter(e => !e.isConcurrentOperation);
    }

    return instances;
  }

  // note that i have left preventOffsets in for now, this was originally so we could load these instances without taking time
  // to de-serialise the dates
  async getAllProjectSpecificOperationInstances(ignoreId?: number): Promise<DailyLogOperationInstance[]> {
    let instances = await this.dbContext.getAll(this.psoTable) as DailyLogOperationInstance[];

    for (let i = 0; i < instances.length; i++) {
      instances[i] = await this.deserializeInstanceTimes(instances[i]);
    }

    if (!!ignoreId) {
      instances = instances.filter(p => p.id !== ignoreId);
    }

    return instances;
  }

  async getPreviousProjectSpecificOperationInstance(dateTime: DateTime): Promise<DailyLogOperationInstance> {
    let instances = await this.getAllProjectSpecificOperationInstances();

    const sortAscending = function (a, b) {
      if (a > b) {
        return +1;
      }
      if (a < b) {
        return -1;
      }
      return 0;
    };

    instances = instances
      .filter(e => e.startTime.dateTime <= dateTime)
      .sort(function (a, b) {
        return sortAscending(a.startTime.dateTime, b.startTime.dateTime);
      });

    if (instances && instances.length > 0) {
      return instances[instances.length - 1];
    }

    return null;
  }

  isTypeAtTimeConcurrent(instance: DailyLogOperationInstance, time: DateTime): boolean {
    if (!instance) {
      return false;
    }

    const switchTimes = instance.switchTimes;

    if (!switchTimes || switchTimes.length < 1) {
      return instance.isConcurrentOperation;
    }

    if (!time || isNaN(time.toMillis())) {
      return switchTimes.sort()[switchTimes.length - 1].isConcurrent;
    }

    let timeBeforeTime = null as TypeSwitch;

    switchTimes.forEach(switchTime => {

      switchTime.date = PtLuxonDate.assignPtLuxonDate(switchTime.date);

      if (!timeBeforeTime) {
        if (switchTime.date.dateTime <= time) {
          timeBeforeTime = cloneDeep(switchTime);
        }
      } else {
        if (switchTime.date.dateTime <= time && timeBeforeTime.date.dateTime <= switchTime.date.dateTime) {
          timeBeforeTime = cloneDeep(switchTime);
        }
      }
    });

    return !timeBeforeTime ? instance.isConcurrentOperation : timeBeforeTime.isConcurrent;
  }

  contractualHierarchyOperationInTime(startTime: DateTime, endTime: DateTime, newStartTime: DateTime, newEndTime: DateTime): boolean {
    if (!newEndTime) {
      if (endTime > newStartTime) {
        return true;
      }
      return false;
    }

    if (!!newStartTime && !!newEndTime && !!startTime && !!endTime) {
      if (Math.abs(newStartTime.toMillis() - newEndTime.toMillis()) < 1000) {
        return false;
      }

      if (Math.abs(startTime.toMillis() - endTime.toMillis()) < 1000) {
        return false;
      }

      if (Math.abs(newStartTime.toMillis() - startTime.toMillis()) < 1000 && Math.abs(newEndTime.toMillis() - endTime.toMillis()) < 1000) {
        return true;
      }

      if (Math.abs(newStartTime.toMillis() - endTime.toMillis()) < 1000 && newEndTime >= endTime) {
        return false;
      }

      if (Math.abs(newEndTime.toMillis() - startTime.toMillis()) < 1000 && newStartTime <= startTime) {
        return false;
      }
    }

    if (newStartTime >= startTime && (newStartTime <= endTime || endTime === null)) {
      return true;
    }

    if (newEndTime >= startTime && newEndTime <= endTime) {
      return true;
    }

    return false;

  }

  findOverlapZone(startTime: DateTime, endTime: DateTime, newStartTime: DateTime, newEndTime: DateTime): StartAndEnd {
    const zone = new StartAndEnd();

    if (!newEndTime) {
      if (endTime > newStartTime) {
        zone.endTime = new PtLuxonDate(endTime);
        zone.startTime = newStartTime < startTime ? new PtLuxonDate(startTime) : new PtLuxonDate(newStartTime);
        return zone;
      }
      return null;
    }

    if (!!newStartTime && !!newEndTime && !!startTime && !!endTime) {
      if (Math.abs(newStartTime.toMillis() - newEndTime.toMillis()) < 1000) {
        return null;
      }

      if (Math.abs(startTime.toMillis() - endTime.toMillis()) < 1000) {
        return null;
      }

      if (Math.abs(newStartTime.toMillis() - startTime.toMillis()) < 1000 && Math.abs(newEndTime.toMillis() - endTime.toMillis()) < 1000) {
        zone.startTime = new PtLuxonDate(startTime);
        zone.endTime = new PtLuxonDate(endTime);
        return zone;
      }

      if (Math.abs(newStartTime.toMillis() - endTime.toMillis()) < 1000 && newEndTime >= endTime) {
        return null;
      }

      if (Math.abs(newEndTime.toMillis() - startTime.toMillis()) < 1000 && newStartTime <= startTime) {
        return null;
      }
    }

    if (newStartTime >= startTime && (newStartTime <= endTime || endTime === null)) {
      zone.startTime = new PtLuxonDate(newStartTime);
      zone.endTime = endTime < newEndTime ? new PtLuxonDate(endTime) : new PtLuxonDate(newEndTime);
      return zone;
    }

    if (newEndTime >= startTime && newEndTime <= endTime) {
      zone.startTime = startTime > newStartTime ? new PtLuxonDate(startTime) : new PtLuxonDate(newStartTime);
      zone.endTime = new PtLuxonDate(newEndTime);
      return zone;
    }

    if (newEndTime >= startTime && newEndTime <= endTime) {
      zone.startTime = startTime > newStartTime ? new PtLuxonDate(startTime) : new PtLuxonDate(newStartTime);
      zone.endTime = new PtLuxonDate(newEndTime);
      return zone;
    }

    if (newStartTime <= startTime && newEndTime >= endTime) {
      zone.startTime = new PtLuxonDate(startTime);
      zone.endTime = new PtLuxonDate(endTime);
      return zone;
    }

    return null;
  }

  operationInTime(startTime: DateTime, endTime: DateTime, newStartTime: DateTime, newEndTime: DateTime): boolean {
    if (!newEndTime) {
      if (endTime > newStartTime) {
        return true;
      }
      return false;
    }

    if (newStartTime >= startTime && (newStartTime < endTime || endTime === null)) {
      return true;
    }

    if (newEndTime > startTime && newEndTime <= endTime) {
      return true;
    }

    return false;
  }

  operationInTimeExclusive(startTime: DateTime, endTime: DateTime, newStartTime: DateTime, newEndTime: DateTime): boolean {
    if (!newEndTime) {
      if (endTime > newStartTime) {
        return true;
      }
      return false;
    }

    if (newStartTime > startTime && newStartTime < endTime) {
      return true;
    }

    if (newEndTime > startTime && newEndTime < endTime) {
      return true;
    }

    return false;
  }

  latestOperationTime(instance: DailyLogOperationInstance): DateTime {
    if (!instance) {
      return null;
    }

    let latestTime = instance.startTime.dateTime;

    const steps = !!instance.stepCollectionInstance ? instance.stepCollectionInstance.stepInstances : null;

    if (!!steps) {
      steps.forEach(step => {

        if (step.startTime.dateTime > latestTime) {
          latestTime = step.startTime.dateTime;
        }

        if (step.endTime.dateTime > latestTime) {
          latestTime = step.endTime.dateTime;
        }

      });
    }

    if (instance.endTime.dateTime > latestTime) {
      latestTime = instance.endTime.dateTime;
    }

    return latestTime;
  }

  operationConcurrentAtTime(instance: DailyLogOperationInstance, time: DateTime): boolean {

    if (!instance.switchTimes) {
      return instance.isConcurrentOperation;
    }

    let viableSwitchTimes = instance.switchTimes;

    if (!!instance.cancelledTime) {
      viableSwitchTimes = instance.switchTimes.filter(p => p.date.dateTime > instance.cancelledTime);
    }

    if (!viableSwitchTimes || viableSwitchTimes.length < 1) {
      return instance.isConcurrentOperation;
    }

    let latestSwitchTime = viableSwitchTimes[0];

    viableSwitchTimes.forEach(element => {
      if (element.date.dateTime > latestSwitchTime.date.dateTime && element.date.dateTime <= time) {
        latestSwitchTime = element;
      }
    });

    return latestSwitchTime.isConcurrent;

  }

  async getOperationsFromLogs(logs: DailyOperationLogRecord[]): Promise<DailyLogOperationInstance[]> {
    if (!logs) {
      return [];
    }

    const operationIds = logs
      .filter(p => p && !!p.operationInstanceId)
      .map(p => p.operationInstanceId)
      .filter((id, index, array) => array.indexOf(id) === index);

    const operations = await this.getProjectSpecificOperationInstances(operationIds);

    // It's not enough to just get PSOs of ops that are in the current log files - if an operation has linked delay
    // ids, and spans multiple days, then those delay operations in other days won't be loaded in memory, that will cause the daily log validation
    // to error as it will try and reference an id we don't have the operation loaded for so we need to add them below
    const linkedDelayIds = operations
      .flatMap(operation => [
        ...(operation.delayOperationApiIds || []),
        ...((operation.pausedTimes || []).map(p => p.delayApiId).filter(id => id != null))
      ])
      .filter((id, index, array) => array.indexOf(id) === index);

    const allIds = [...operationIds];

    linkedDelayIds.forEach(id => {
      if (!allIds.includes(id)) {
        allIds.push(id);
      }
    });

    return await this.getProjectSpecificOperationInstances(allIds);
  }

  createStepCollectionInstance(stepCollection: StepCollection): StepCollectionInstance {
    if (stepCollection === null) {
      return null;
    }

    stepCollection.steps = stepCollection.steps.sort(function (a, b) {
      return a.defaultNumber - b.defaultNumber;
    });

    return {
      name: cloneDeep(stepCollection.name),
      stepInstances: cloneDeep(stepCollection.steps.map(step => this.createStepInstance(step))),
      operationIds: cloneDeep(stepCollection.operationIds),
      ordinal: cloneDeep(stepCollection.ordinal)
    };
  }

  createStepInstance(step: Step): StepInstance {
    return {
      localId: null,
      number: cloneDeep(step.defaultNumber),
      group: cloneDeep(step.defaultGroup),
      secondsDelay: cloneDeep(step.defaultSecondsDelay),
      secondsSpan: cloneDeep(step.defaultSecondsSpan),
      isConcurrent: cloneDeep(step.defaultIsConcurrent),
      step: cloneDeep(step),
      id: null,
      stepId: cloneDeep(step.id),
      guid: cloneDeep(step.guid),
      sortTime: null,
      exclude: null,
      comment: null,
      startTime: null,
      endTime: null,
      startConfirmed: null,
      endConfirmed: null,
      startTimeToShow: null,
      endTimeToShow: null,
      startTimeOffset: null,
      endTimeOffset: null,
      jackingEventGuid: null,
      canConfirmTimes: false,
      significantPoint: cloneDeep(step.significantPoint),
      startDisabledForLockedDay: false,
      endDisabledForLockedDay: false
    };
  }

  getPreviousTimeFromSteps(stepCollection: StepCollectionInstance, stepInstance: StepInstance): DateTime {
    if (stepCollection && stepInstance) {
      const previousSteps = stepCollection.stepInstances
        .filter(e => !e.exclude && e.number < stepInstance.number)
        .sort(function (a, b) {
          return b.number - a.number;
        });

      for (const step of previousSteps) {
        if (step.endTime && step.endTime.dateTime) {
          return step.endTime.dateTime;
        } else if (step.startTime && step.startTime.dateTime) {
          return step.startTime.dateTime;
        }
      }
    }


    return null;
  }

  async operationIsLinkedToVoyageLogStart(operationInstanceId: number) {
    const voyageLog = await this.voyageLogService.getVoyageLogForStartOperationId(operationInstanceId);
    return voyageLog ? voyageLog.startOperationId === operationInstanceId : false;
  }

  async operationIsLinkedToVoyageLogEnd(operationInstanceId: number) {
    const voyageLog = await this.voyageLogService.getVoyageLogForEndOperationId(operationInstanceId);
    return voyageLog ? voyageLog.endOperationId === operationInstanceId : false;
  }

  // if endOfDay and instance end time is null, then use the end of day as the latest instance time
  getLatestTimeInOperationInstance(instance: DailyLogOperationInstance, endOfDay?: PtLuxonDate): PtLuxonDate {
    const instanceCopy = cloneDeep(instance) as DailyLogOperationInstance;

    if (instanceCopy.endTime && instanceCopy.endTime.dateTime) {
      return instanceCopy.endTime;
    } else if (endOfDay && endOfDay.dateTime) {
      return endOfDay;
    }

    const sortAscending = function (a, b) {
      if (a > b) {
        return +1;
      }
      if (a < b) {
        return -1;
      }
      return 0;
    };

    // filter out steps with no start time, then sort by start, then return the latest step
    if (instanceCopy.stepCollectionInstance && instanceCopy.stepCollectionInstance.stepInstances && instanceCopy.stepCollectionInstance.stepInstances.length > 0) {
      instanceCopy.stepCollectionInstance.stepInstances = instanceCopy.stepCollectionInstance.stepInstances.filter(e => e.startTime && e.startTime.dateTime);

      if (instanceCopy.stepCollectionInstance.stepInstances.length === 0)
        return instanceCopy.startTime;

      instanceCopy.stepCollectionInstance.stepInstances = instanceCopy.stepCollectionInstance.stepInstances.sort(function (a, b) {
        return sortAscending(a.startTime.dateTime.toMillis(), b.startTime.dateTime.toMillis());
      });

      return instanceCopy.stepCollectionInstance.stepInstances[instanceCopy.stepCollectionInstance.stepInstances.length - 1].startTime;
    }

    return instanceCopy.startTime;
  }

  // add in app offset, optionally remove the system offset
  async deserializeInstanceTimes(
    instance: DailyLogOperationInstance,
  ): Promise<DailyLogOperationInstance> {
    this.normalizeResponsibilityTypeId(instance);

    if (instance.projectSpecificOperation?.id) {
      const cachedOperation = await this.getOperation(instance.projectSpecificOperation.id);
      if (cachedOperation && cachedOperation.responsibilityRequired !== undefined) {
        instance.projectSpecificOperation.responsibilityRequired = cachedOperation.responsibilityRequired;
        instance.projectSpecificOperation.responsibilityOptionalStageIds = cachedOperation.responsibilityOptionalStageIds;
      }
    }

    if (instance.startTime) {
      instance.startTime = await this.dateParserService.deserializePtLuxonDate(instance.startTime, this.timeZoneService, instance.startTimeOffset);
      await instance.startTime.checkForMultipleOffsets(this.timeZoneService, instance.startTimeOffset);
      instance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.startTime);
    }

    if (instance.endTime) {
      instance.endTime = await this.dateParserService.deserializePtLuxonDate(instance.endTime, this.timeZoneService, instance.endTimeOffset);
      await instance.endTime.checkForMultipleOffsets(this.timeZoneService, instance.endTimeOffset);
      instance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.endTime);
    }

    if (instance.stepCollectionInstance && instance.stepCollectionInstance.stepInstances) {
      for (let i = 0; i < instance.stepCollectionInstance.stepInstances.length; i++) {
        const stepCollection = instance.stepCollectionInstance.stepInstances;

        if (stepCollection[i].startTime) {
          stepCollection[i].startTime = await this.dateParserService.deserializePtLuxonDate(stepCollection[i].startTime, this.timeZoneService, stepCollection[i].startTimeOffset);
          await stepCollection[i].startTime.checkForMultipleOffsets(this.timeZoneService, stepCollection[i].startTimeOffset);
          stepCollection[i].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(stepCollection[i].startTime);
        }

        if (stepCollection[i].endTime) {
          stepCollection[i].endTime = await this.dateParserService.deserializePtLuxonDate(stepCollection[i].endTime, this.timeZoneService, stepCollection[i].endTimeOffset);
          await stepCollection[i].endTime.checkForMultipleOffsets(this.timeZoneService, stepCollection[i].endTimeOffset);
          stepCollection[i].endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(stepCollection[i].endTime);
        }
      }
    }

    if (instance.comments) {
      for (let i = 0; i < instance.comments.length; i++) {
        if (instance.comments[i].startTimeDate) {
          instance.comments[i].startTimeDate = await this.dateParserService.deserializePtLuxonDate(instance.comments[i].startTimeDate, this.timeZoneService, instance.comments[i].startTimeOffset);
          await instance.comments[i].startTimeDate.checkForMultipleOffsets(this.timeZoneService, instance.comments[i].startTimeOffset);
          instance.comments[i].startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.comments[i].startTimeDate);
        }

        if (instance.comments[i].endTimeDate) {
          instance.comments[i].endTimeDate = await this.dateParserService.deserializePtLuxonDate(instance.comments[i].endTimeDate, this.timeZoneService, instance.comments[i].endTimeOffset);
          await instance.comments[i].endTimeDate.checkForMultipleOffsets(this.timeZoneService, instance.comments[i].endTimeOffset);
          instance.comments[i].endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.comments[i].endTimeDate);
        }
      }
    }

    if (instance.switchTimes) {
      for (let i = 0; i < instance.switchTimes.length; i++) {
        if (instance.switchTimes[i].date) {
          instance.switchTimes[i].date = await this.dateParserService.deserializePtLuxonDate(instance.switchTimes[i].date, this.timeZoneService, instance.switchTimes[i].dateOffset);
          instance.switchTimes[i].date.multipleOffsets = await this.timeZoneService
            .checkForNegativeTimeZone(instance.switchTimes[i].date.dateTime);
          instance.switchTimes[i].dateToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.switchTimes[i].date);
        }
      }
    }

    if (instance.pausedTimes) {
      for (let i = 0; i < instance.pausedTimes.length; i++) {
        if (instance.pausedTimes[i].startDate) {
          instance.pausedTimes[i].startDate = await this.dateParserService
            .deserializePtLuxonDate(instance.pausedTimes[i].startDate, this.timeZoneService, instance.pausedTimes[i].startDateOffset);
          instance.pausedTimes[i].startDate.multipleOffsets = await this.timeZoneService
            .checkForNegativeTimeZone(instance.pausedTimes[i].startDate.dateTime);
        }

        if (instance.pausedTimes[i].endDate) {
          instance.pausedTimes[i].endDate = await this.dateParserService
            .deserializePtLuxonDate(instance.pausedTimes[i].endDate, this.timeZoneService, instance.pausedTimes[i].endDateOffset);
          instance.pausedTimes[i].endDate.multipleOffsets = await this.timeZoneService
            .checkForNegativeTimeZone(instance.pausedTimes[i].endDate.dateTime);
        }
      }
    }

    if (instance.cancelledTime) {
      instance.cancelledTime = await this.dateParserService.deserializeDate(instance.cancelledTime, true, this.timeZoneService, instance.cancelledTimeOffset);
    }

    if (instance.cancelledTimePtDate) {
      instance.cancelledTimePtDate = await this.dateParserService.deserializePtLuxonDate(instance.cancelledTimePtDate, this.timeZoneService, instance.cancelledTimeOffset);
    }

    return instance;
  }

  private normalizeResponsibilityTypeId(instance: DailyLogOperationInstance): void {
    if (!instance) {
      return;
    }

    const responsibilityTypeId = (instance as any).responsibilityTypeId;

    if (typeof responsibilityTypeId !== 'string') {
      return;
    }

    switch (responsibilityTypeId.trim().toLowerCase()) {
      case 'client':
      case '1':
        instance.responsibilityTypeId = ResponsibilityType.Client;
        break;
      case 'owner':
      case '2':
        instance.responsibilityTypeId = ResponsibilityType.Owner;
        break;
    }
  }

  async deserializeOperationMetaData(metaData: OperationMetadata): Promise<OperationMetadata> {
    if (metaData && metaData.stepCollections) {
      for (let i = 0; i < metaData.stepCollections.length; i++) {
        if (metaData.stepCollections[i].stepInstances) {
          for (let j = 0; j < metaData.stepCollections[i].stepInstances.length; j++) {
            if (metaData.stepCollections[i].stepInstances[j].startTime) {
              metaData.stepCollections[i].stepInstances[j].startTime = await this.dateParserService
                .deserializePtLuxonDate(metaData.stepCollections[i].stepInstances[j].startTime, this.timeZoneService);
            }

            if (metaData.stepCollections[i].stepInstances[j].endTime) {
              metaData.stepCollections[i].stepInstances[j].endTime = await this.dateParserService
                .deserializePtLuxonDate(metaData.stepCollections[i].stepInstances[j].endTime, this.timeZoneService);
            }

            if (metaData.stepCollections[i].stepInstances[j].comment) {
              if (metaData.stepCollections[i].stepInstances[j].comment.startTimeDate) {
                metaData.stepCollections[i].stepInstances[j].comment.startTimeDate = await this.dateParserService
                  .deserializePtLuxonDate(metaData.stepCollections[i].stepInstances[j].comment.startTimeDate, this.timeZoneService);
              }

              if (metaData.stepCollections[i].stepInstances[j].comment.endTimeDate) {
                metaData.stepCollections[i].stepInstances[j].comment.endTimeDate = await this.dateParserService
                  .deserializePtLuxonDate(metaData.stepCollections[i].stepInstances[j].comment.endTimeDate, this.timeZoneService);
              }
            }
          }
        }
      }
    }

    return metaData;
  }

  async checkOperationInstanceHasComments(operationInstanceId: number): Promise<boolean> {
    const instance = await this.getProjectSpecificOperationInstance(operationInstanceId);

    return instance.startComment != null
      || instance.endComment != null
      || this.checkOperationInstanceHasStandaloneComments(instance)
      || this.checkOperationInstanceHasStepComments(instance);
  }

  private checkOperationInstanceHasStandaloneComments(instance: DailyLogOperationInstance): boolean {
    return instance.comments && instance.comments.length > 0;
  }

  private checkOperationInstanceHasStepComments(instance: DailyLogOperationInstance): boolean {
    return instance.stepCollectionInstance
      && instance.stepCollectionInstance.stepInstances
      && instance.stepCollectionInstance.stepInstances.length > 0
      && instance.stepCollectionInstance.stepInstances.some(e => e.comment !== null);
  }
}



// WEBPACK FOOTER //
// ./src/services/operations/operation/operation.service.ts